import { useContext, useEffect } from 'react';
import { UNSAFE_DataRouterContext, useBlocker, useLocation } from 'react-router-dom';

/** Local stage/tab/step changes retain the mounted form; it owns its internal step guard. */
function RouterGuard({ dirty, scopeDirty }: { dirty: boolean; scopeDirty: boolean }) {
  const location = useLocation();
  const blocker = useBlocker(({ nextLocation }) => {
    if (!dirty) return false;
    const current = new URLSearchParams(location.search);
    const next = new URLSearchParams(nextLocation.search);
    if (nextLocation.pathname === location.pathname) {
      if (next.get('providerScope') === current.get('providerScope')) return false;
      // Organization services stay mounted across provider changes; only these drafts reset.
      return (
        scopeDirty &&
        !window.confirm('Discard unsaved intake or credentialing changes and change scope?')
      );
    }
    return !window.confirm('Discard unsaved Intake Hub changes and continue?');
  });
  useEffect(() => {
    if (blocker.state === 'blocked') blocker.reset();
  }, [blocker]);
  return null;
}

export function AdminIntakeNavigationGuard({
  dirty,
  scopeDirty,
}: {
  dirty: boolean;
  scopeDirty: boolean;
}) {
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  useEffect(() => {
    if (!dirty) return;
    const preventUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', preventUnload);
    return () => window.removeEventListener('beforeunload', preventUnload);
  }, [dirty]);
  // MemoryRouter presentation stories have no router blocker; the real app uses a data router.
  return dataRouter ? <RouterGuard dirty={dirty} scopeDirty={scopeDirty} /> : null;
}
